<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }"
            name="activator"
            data-cy="list-podNodeItem-actions-moveSelected-activator"
      />
    </template>

    <v-card data-cy="dialog-moveSelected-modal">
      <v-card-title
        class="d-flex justify-space-between"
        data-cy="dialog-moveSelected-title"
      >
        {{ $t('routes.node.moveDialogMoveSelected', itemsCount) }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable  mt-4">
        <div v-if="waitingForFtpCheck" class="text-center pt-5">
          <v-progress-circular class="float-left" indeterminate color="primary" :aria-label="$t('routes.node.loading')"></v-progress-circular>
          <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
        </div>

        <template v-if="!waitingForFtpCheck">
          <div v-if="affectedFtpUsers.length > 0" class="tw-warning" style="opacity: 0.65">
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.node.moveFtpUsersWarning') }}</span>
            <ul class="mx-5 mt-1">
              <li v-for="group in affectedFtpUsersByNode" :key="group.nodeId">
                <router-link :to="'/node/' + group.nodeId">{{ group.nodeName }}</router-link>
                ({{ group.usernames.join(', ') }})
              </li>
            </ul>
          </div>

          <template v-else>
            <div v-if="browseNode && !waitingForFtpCheck" class="pb-2">
              <FolderCreateInline
                :parent-node="browseNode"
                data-cy-prefix="dialog-moveSelected"
                @created="handleFolderCreated"
              />
            </div>

            <div v-if="itemsCount > 0">
              <v-text-field
                :placeholder="$t('routes.node.typeFolderName')"
                variant="solo-filled"
                density="compact"
                hide-details
                append-inner-icon="mdi-magnify"
                class="mb-2"
                v-model="searchQuery"
              />
            </div>

            <div v-if="browseNode && !waitingForFtpCheck" class="pb-2">
              <div v-if="isArchived" class="mt-4">
                <v-checkbox
                  v-model="archiveItems"
                  v-bind:label="$t('routes.node.archiveItems')"
                  hide-details
                />
              </div>
            </div>

          <div v-if="itemsCount === 0  && shouldShowNoItems"
                class="bg-error"
                data-cy="dialog-moveSelected-noItems"
          >
            {{ $t('routes.node.moveDialogNoItems') }}
          </div>

          <div v-if="errorMessage && errorCode !== 'ERR233'" class="bg-error my-2" data-cy="dialog-moveSelected-errorMessage">
            <span>{{ errorMessage }}</span>
          </div>

          <div v-if="errorCode === 'ERR233' && errorItems" class="bg-error my-2">
            {{ $t('backend.ERR233', errorParams) }}
            <span v-if="errorItems.length === 1">
              {{ $t('routes.node.depthExceededTooDeep', errorItems[0].children.length) }}
              <DepthExceededChildren :children="errorItems[0].children" @navigate="dialog = false" />
            </span>
            <template v-else>
              {{ $t('routes.node.depthExceededTooDeep', 2) }}
              <ul class="mx-5 mt-1">
                <li v-for="item in errorItems" :key="item.parent.id">
                  <strong>{{ item.parent.name }}</strong> &gt;
                  <DepthExceededChildren :children="item.children" @navigate="dialog = false" />
                </li>
              </ul>
            </template>
          </div>

          <div v-for="infoMsg in infoMessages" :key="infoMsg" class="bg-info my-2">
            {{ infoMsg }}
          </div>

          <v-list
            v-if="itemsCount > 0"
            density="compact"
            :selected="listGroupSelected"
            :aria-label="$t('routes.node.destinationFolder')"
          >
            <div v-if="browseNode" class="move-selected-dialog-list-subheader"
                  data-cy="dialog-moveSelected-destination">
              <div>
                {{ $t('routes.node.moveDialogTo') }}
              </div>
              <p class="font-weight-bold">
                {{ browseNode.name }}
              </p>
            </div>
            <div class="move-selected-dialog-list-subheader" v-else>
              {{ $t('routes.node.moveDialogSelectDestination') }}
            </div>

            <div v-if="nodeList.length > 0" data-cy="dialog-moveSelected-listGroup">
              <v-list-item
                v-for="node in nodeList"
                v-bind:key="node.id"
                v-on:click="displayNode(node.id)"
                v-bind:disabled="isNodeDisabled(node)"
                data-cy="dialog-moveSelected-listItem">

                <template v-slot:prepend>
                  <v-icon>{{ getNodeIconName(node) }}</v-icon>
                </template>

                <div class="ml-1" data-cy="dialog-moveSelected-itemContent">
                  <v-list-item-title>
                    <v-tooltip location="bottom" z-index="1200" :aria-label="node.name">
                      <template v-slot:activator="{ props }">
                        <span

                          v-bind="props"
                        >
                          {{ node.name }}
                        </span>
                      </template>
                      <span>{{ node.name }}</span>
                    </v-tooltip>
                  </v-list-item-title>
                </div>
              </v-list-item>
            </div>
            <div v-else>
              <v-list-item disabled>
                {{ $t('routes.node.moveDialogNoMoreSubFolders') }}
              </v-list-item>
            </div>
          </v-list>
          </template>
        </template>

      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="!!browseNode"
          v-on:click="displayNode(browseNode.parentid)"
          variant="text"
          data-cy="dialog-moveSelected-back-btn"
        >
          <v-icon>mdi-arrow-left</v-icon>
          {{ $t('routes.node.moveDialogBack') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" elevation=0 v-bind:disabled="submitDisabled" @click="submit"
               v-if="affectedFtpUsers.length === 0"
               data-cy="dialog-moveSelected-submit-btn">
          <span v-if="!showingOKtoConfirm">{{ $t('common.move') }}</span>
          <span v-else>{{ $t('common.ok') }}</span>
        </v-btn>
        <v-btn @click="dialog = false" variant="text" data-cy="dialog-moveSelected-cancel-btn">
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import DepthExceededChildren from '@/components/DepthExceededChildren.vue';
import FolderCreateInline from '@/components/dialogs/FolderCreateInline.vue';

export default {
  mixins: [DialogActionKeys, DialogModel],
  components: { DepthExceededChildren, FolderCreateInline },
  name: "MoveSelectedDialog",
  props: {
    nodes: {
      type: Array,
      required: false,
    },
    pods: {
      type: Array,
      required: false
    },
    isDisabled: {
      type: Boolean,
      required: false,
    },
  },
  data: function() {
    return {
      errorMessage: '',
      conflictingNodeNames: [],
      errorItems: null,
      errorCode: null,
      errorParams: {},
      infoMessages: [],
      browseNode: null,
      listGroupSelected: null,
      preventNoItems: false,
      archiveItems: false,
      preventDoubleClick: false,
      showingOKtoConfirm: false,
      searchQuery: '',
      isGettingNodeInfo: false,
      waitingForFtpCheck: false,
      affectedFtpUsers: [],
    }
  },
  watch: {
    dialog: async function(newValue) {
      this.resetDialog();
      if (!newValue) {
        this.browseNode = null;
        this.affectedFtpUsers = [];
        this.waitingForFtpCheck = false;
      }
      if (newValue) {
        this.preventNoItems = false;
        if (this.nodes && this.nodes.length > 0) {
          this.waitingForFtpCheck = true;
          const nodeStore = useNodeStore();
          const nodeIDs = this.nodes.map((node) => node.id);
          try {
            const ftpUsers = await nodeStore.getAffectedFtpUsers(nodeIDs);
            this.affectedFtpUsers = ftpUsers || [];
          } catch (error) {
            console.log('Error checking affected FTP users:', error);
            this.affectedFtpUsers = [];
          } finally {
            this.waitingForFtpCheck = false;
          }
        }
      }
    },
    browseNode: function (newValue) {
      if (!newValue) this.archiveItems = false;
      if (newValue && newValue.archived && newValue.archived === 1) {
        this.archiveItems = true;
      }
    },
  },
  methods: {
    resetDialog: function() {
      // this.dialog = false;
      this.errorMessage = '';
      this.infoMessages = [];
      this.conflictingNodeNames = [];
      this.errorItems = null;
      this.errorCode = null;
      this.errorParams = {};
      this.listGroupSelected = null;
      this.preventDoubleClick = false;
      this.showingOKtoConfirm = false;
    },
    displayNode: async function(nodeID) {
      this.listGroupSelected = null;
      this.showingOKtoConfirm = false;
      this.infoMessages = [];

      // sometimes this.browseNode.parentid is null
      if (nodeID) {
        this.isGettingNodeInfo = true;

        try {
          const nodeStore = useNodeStore();
          this.browseNode = await nodeStore.getNodeInfo(nodeID);
        } finally {
          this.isGettingNodeInfo = false;
        }

        this.listGroupSelected = null;
      } else {
        this.browseNode = null;
      }
    },
    async handleFolderCreated(createdNode) {
      if (!createdNode?.id) {
        return;
      }

      await this.displayNode(createdNode.id);
    },
    submit: async function() {
      if (this.showingOKtoConfirm) {
        this.submitNoValidate();
        return;
      }

      this.submitValidate();
    },
    submitValidate: async function() {
      this.errorMessage = '';
      this.infoMessages = [];
      this.conflictingNodeNames = [];
      this.preventNoItems = true;
      if (!this.browseNode || this.itemsCount === 0 || this.preventDoubleClick) {
        return;
      }

      if (this.nodes.length === 0) {
        // if no nodes selected, we have only pods. So we need to move the pods only.
        try {
          await this.submitNoValidate();
        } catch (error) {
          this.errorMessage = this.$localizeMessage(error?.response?.data?.error, this.$t('backend.system-error'));
          this.preventNoItems = true;
        }
      } else {
        this.preventDoubleClick = true;

        const payload = {
          targetnodeid: this.browseNode.id,     // destination
          nodeid: this.getCurrentNode.id,       // current directory
          nodes: this.nodes.map(x => x.id),
          archive: this.archiveItems ? 1 : 0,
          validateRequestOnly: 1,
        };

        try {
          const nodeStore = useNodeStore();
          const response = await nodeStore.moveManyNodes(payload)
          if (response?.validation === true) {
            this.preventDoubleClick = false;

            const responseMessages = response.messages.flat();
            if (responseMessages.length > 0) {
              for (const message of responseMessages) {
                const localizedMsg = this.$localizeMessage(message);
                this.infoMessages.push(localizedMsg);
              }

              this.showingOKtoConfirm = true;
            } else {
              await this.submitNoValidate();
            }
          }
        } catch (error) {
          const err = error?.response?.data?.error;
          if (err?.code === 'ERR233') {
            this.errorCode = err.code;
            this.errorParams = { ...err.params };
            if (err.extra?.items) {
              this.errorItems = err.extra.items;
            }
          } else {
            this.errorMessage = this.$localizeMessage(err, this.$t('backend.system-error'));
          }
          this.preventNoItems = true;
        }
      }
    },
    submitNoValidate: async function() {
      this.errorMessage = '';
      this.infoMessages = [];
      this.preventNoItems = true;
      if (!this.browseNode || this.itemsCount === 0 || this.preventDoubleClick) {
        return;
      }

      this.preventDoubleClick = true;

      let promises = [];

      if (this.nodes.length > 0) {
        const payload = {
          targetnodeid: this.browseNode.id,     // destination
          nodeid: this.getCurrentNode.id,       // current directory
          nodes: this.nodes.map((x) => x.id),
          archive: this.archiveItems ? 1 : 0,
          validateRequestOnly: 0,
        };

        const nodeStore = useNodeStore();
        promises.push(nodeStore.moveManyNodes(payload));
      }

      if (this.pods.length > 0) {
        const payload = {
          targetnodeid: this.browseNode.id,     // destination
          nodeid: this.getCurrentNode.id,       // current directory
          pods: this.pods.map((x) => x.id),
          archive: this.archiveItems ? 1 : 0,
        };

        const podStore = usePodStore();
        promises.push(podStore.moveManyPods(payload));
      }

      try {
        await Promise.all(promises);
        this.dialog = false;
        this.preventNoItems = true;
        this.preventDoubleClick = false;

        const podStore = usePodStore();
        podStore.deselectAllPods();
        const nodeStore = useNodeStore();
        nodeStore.deselectAllNodes();
      } catch (error) {
        const err = error?.response?.data?.error;
        if (err?.code === 'ERR233') {
          this.errorCode = err.code;
          this.errorParams = { ...err.params };
          if (err.extra?.items) {
            this.errorItems = err.extra.items;
          }
        } else {
          this.errorMessage = this.$localizeMessage(err, this.$t('backend.system-error'));
        }
        this.preventNoItems = true;
      }
    }
  },
  computed: {
    nodeList: function() {
      if (this.browseNode) {
        if (!this.browseNode.children) {
          return [];
        }

        const childNodes = [];

        // filter out archived nodes
        if (Array.isArray(this.browseNode.children) && this.browseNode.children.length > 0) {
          for (let i = 0; i < this.browseNode.children.length; i++) {
            const childNode = this.browseNode.children[i];

            if (!childNode.archived) {
              childNodes.push(childNode);
            }
          }
        }

        // filter by search query
        if (this.searchQuery.length >= 1) {
          const cleanQuery = this.searchQuery.toLowerCase();

          const filteredNodes = childNodes.filter((node) => {
            return node.name.toLowerCase().includes(cleanQuery);
          });

          // sort the nodes by name
          return (filteredNodes.length > 0) ? filteredNodes.sort((a, b) => a.name.localeCompare(b.name)) : [];
        }

        return childNodes.sort((a, b) => a.name.localeCompare(b.name));
      }

      if (this.accessPoints && Array.isArray(this.accessPoints) && this.accessPoints.length > 0) {
        const accessPoints = this.accessPoints;
        return accessPoints.sort((a, b) => a.name.localeCompare(b.name)) || [];
      }

      return [];
    },
    accessPoints: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    getViewPod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getCurrentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    itemsCount: function() {
      return this.nodes.length + this.pods.length;
    },
    getNodeType: function() {
      const commonStore = useCommonStore();
      const TYPES = commonStore.nodeTypes;

      let self = this;
      return (node) => {
        switch (node.type) {
          case TYPES.FOLDER: return self.$t('routes.node.typeFolder');
          case TYPES.PROJECT: return self.$t('routes.node.typeProject');
          case TYPES.CLIENT: return self.$t('routes.node.typeClient');
          case TYPES.TENANT: return self.$t('routes.node.typeTenant');
          default: return self.$t('routes.node.typeUnknown');
        }
      }
    },
    getNodeIconName: function() {
      return (node) => {
        let iconName;
        switch (node.type) {
          case 2: iconName = "mdi-briefcase-clock-outline"; break;
          case 3: iconName = "tw tw-office-building"; break;
          case 4: iconName = "mdi-home-assistant"; break;
          default: iconName = "mdi-folder-outline"; break;
        }

        if (node.archived == 1 || node.archived == 2) {
          iconName = "mdi-package-down";
        }

        return iconName;
      };
    },
    isNodeDisabled: function() {
      const commonStore = useCommonStore();
      const rootNodeUUID = commonStore.rootFolderId;
      const TYPES = commonStore.nodeTypes;

      const authStore = useAuthStore();
      const rootNodeId = authStore.rootNodeId;

      let nodeIDs = this.nodes.map(x => x.id);
      let selectedNodeTypes = this.nodes.map(x => x.type);

      return (node) => {
        // Disable the selected nodes
        return (nodeIDs.indexOf(node.id) >= 0)
          // The parent of the tenant node could be only the root node.
          || (selectedNodeTypes.indexOf(TYPES.TENANT) >=0 && node.id !== rootNodeUUID)
          // The parent of the client node could be only a tenant.
          || (selectedNodeTypes.indexOf(TYPES.CLIENT) >=0  && node.type !== TYPES.TENANT && node.id !== rootNodeUUID)
          // The node couldn't be found outside the current tenant.
          || !node.rootpath.startsWith(`/${rootNodeId}`);
      }
    },
    submitDisabled: function() {
      if (!this.browseNode || this.isGettingNodeInfo || this.itemsCount === 0 || this.preventDoubleClick || this.waitingForFtpCheck || this.affectedFtpUsers.length > 0) return true;

      let parentID = this.pods.length > 0 ? this.pods[0].nodeid : this.nodes[0].parentid;
      if (parentID === this.browseNode.id) return true;    // cannot move in the current directory

      return false;
    },
    shouldShowNoItems: function() {
      return !this.errorMessage && !this.preventNoItems;
    },
    isArchived: function() {
      return (this.browseNode?.archived === 1);
    },
    affectedFtpUsersByNode: function() {
      const groups = {};
      for (const user of this.affectedFtpUsers) {
        if (!groups[user.nodeId]) {
          groups[user.nodeId] = { nodeId: user.nodeId, nodeName: user.nodeName, usernames: [] };
        }
        groups[user.nodeId].usernames.push(user.username);
      }
      return Object.values(groups);
    },
  },
}
</script>

<style>
.v-subheader > .move-selected-dialog-list-subheader {
  font-size: 1rem;
}

.v-subheader.v-theme--light > .move-selected-dialog-list-subheader {
  color: rgba(0, 0, 0, 0.8);
}
</style>
